// src/pages/Grow/Intakes/Components/IntakesListView.tsx
/**
 * The public course-registration intake queue.
 *
 * The Grow mirror of `Counselling/Requests`: public sign-ups land as staging rows, and an admin either
 * approves (creating the `Registration` and its participant members) or rejects with a reason.
 *
 * ⚠️ **Paged server-side.** `RegistrationIntakeFilterModel` carries real
 * `courseID / dateID / intakeStatusID / search / skip / take` and the response carries `totalCount`.
 * There is no intake streaming hook, and this queue grows with every public submission.
 *
 * ⚠️ **Sorting is server-side.** `RegistrationIntakeFilterModel` carries `sortBy`/`sortDir`, so the
 * header arrows reorder the **whole queue**, not the ten rows on screen. Do **not** reach for
 * `sortRows` from `@/utils/tableSort` here: it sorts only the loaded page, which would silently
 * misrepresent everything past it. Only the columns the API can actually sort are marked sortable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon, Badge, Button, Card, Checkbox, Drawer, Group, SimpleGrid, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import {
  IconAdjustments, IconCheck, IconEye, IconListDetails, IconRefresh, IconSearch,
} from '@tabler/icons-react';
import {
  Flex, Select, TextInput, CCMSDataTable, StatusKPICard, StatusKPISkeleton, getStatusIcon,
  ListPagination, DatePickerInput,
} from '@/components';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import {
  useRegistrationIntakeService, useRegistrationIntakeStatusService, useACCourseService,
  useLocationService, useACCourseSettingService,
  type RegistrationIntakeListView as IntakeRow,
  type RegistrationIntakeFilterModel,
  type RegistrationIntakeModelView,
} from '@actschurch/ccms-sdk';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useLookupStatuses } from '@/pages/Overview/Hooks/useLookupStatuses';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { formatSDKDate, toSDKDate, toSDKDateTime, endOfDay } from '@/utils/dateTime';
import { INTAKE_STATUS } from '@/pages/Grow/intakeStatusIds';
import ApproveIntakeModal from './ApproveIntakeModal';
import BulkDeleteControls from '@/components/common/BulkDeleteControls';
import RowDeleteAction from '@/components/common/RowDeleteAction';
import { useBulkDelete } from '@/hooks/useBulkDelete';
import { useModuleDeleteSettings } from '@/hooks/useModuleDeleteSettings';
import { AccessControl } from '@/components';

const PAGE_SIZE = 10;

/**
 * Table column key → the value `RegistrationIntakeFilterModel.sortBy` accepts.
 *
 * ⚠️ A column absent from this map must **not** be marked `sortable`. The server rejects (or silently
 * ignores) anything else, and an arrow that does nothing is worse than no arrow. `startDisplay` is the
 * one that differs: the column renders a formatted string, the API orders by the underlying timestamp.
 */
const SORT_KEY_BY_COLUMN: Record<string, string> = {
  fullName: 'fullName',
  courseName: 'courseName',
  startDisplay: 'startDateTime',
  submittedDateTime: 'submittedDateTime',
  statusName: 'statusName',
};

export default function IntakesListView() {
  const navigate = useNavigate();
  const intakeService = useRegistrationIntakeService();
  const intakeStatusService = useRegistrationIntakeStatusService();
  const courseService = useACCourseService();
  const locationService = useLocationService();
  const settingService = useACCourseSettingService();
  const deleteSettings = useModuleDeleteSettings(settingService, 'Intakes');
  const bulk = useBulkDelete(intakeService, deleteSettings.allowBulkDelete, 'intake');

  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [draftCourse, setDraftCourse] = useState<string | null>(null);
  const [draftLocation, setDraftLocation] = useState<string | null>(null);
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);
  const [draftTo, setDraftTo] = useState<Date | null>(null);
  const [applied, setApplied] = useState<Partial<RegistrationIntakeFilterModel>>({});

  /** Server-side sort. Null until the admin clicks a header; the API then defaults to newest first. */
  const [sort, setSort] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' } | null>(null);

  const statuses = useLookupStatuses(intakeStatusService);
  const [courses, setCourses] = useState<{ value: string; label: string }[]>([]);
  const [locations, setLocations] = useState<{ value: string; label: string }[]>([]);

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [allCount, setAllCount] = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);

  /**
   * The intake being approved from the queue, or null.
   *
   * ⚠️ The wizard needs the full `RegistrationIntakeModelView` — participants, course, dates — and the
   * row is only a `RegistrationIntakeListView`. So the action fetches the detail first rather than
   * opening a wizard onto a half-populated record.
   */
  const [approveIntake, setApproveIntake] = useState<RegistrationIntakeModelView | null>(null);
  const [openingFor, setOpeningFor] = useState<number | null>(null);

  /** Drops a slow early response that would otherwise overwrite a fast later one. */
  const requestSeq = useRef(0);

  const fetchRows = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await intakeService.listView({
        ...applied,
        ...(sort ?? {}),
        search: debouncedSearch.trim() || undefined,
        skip: (page - 1) * pageSize,
        take: pageSize,
      });
      if (seq !== requestSeq.current) return;
      setRows(res?.items ?? []);
      setTotalCount(res?.totalCount ?? 0);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      console.error('[Intakes] failed to load:', e);
      setRows([]);
      setTotalCount(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [intakeService, applied, sort, debouncedSearch, page, pageSize]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    courseService.getAllViews()
      .then((list) => setCourses(
        (Array.isArray(list) ? list : []).map((c) => ({
          value: String(c.courseID),
          label: c.name ?? `Course ${c.courseID}`,
        })),
      ))
      .catch((e) => console.error('[Intakes] failed to load courses:', e));
  }, [courseService]);

  useEffect(() => {
    locationService.getAll()
      .then((list) => setLocations(
        (Array.isArray(list) ? list : [])
          .filter((l) => l.isActive)
          .map((l) => ({ value: String(l.locationID), label: l.name })),
      ))
      .catch((e) => console.error('[Intakes] failed to load locations:', e));
  }, [locationService]);

  /** Global counts: they describe the queue, not the active filter. */
  const fetchCounts = useCallback(async () => {
    if (statuses.length === 0) return;
    setCountsLoading(true);
    try {
      const [all, ...perStatus] = await Promise.all([
        intakeService.listView({ take: 1, skip: 0 }),
        ...statuses.map((s) =>
          intakeService.listView({ take: 1, skip: 0, intakeStatusID: String(s.statusID) })),
      ]);
      setAllCount(all?.totalCount ?? 0);
      const next: Record<string, number> = {};
      statuses.forEach((s, i) => { next[String(s.statusID)] = perStatus[i]?.totalCount ?? 0; });
      setCounts(next);
    } catch (e) {
      console.error('[Intakes] failed to load counts:', e);
    } finally {
      setCountsLoading(false);
    }
  }, [intakeService, statuses]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  /** ⚠️ Pending is `0`, so status stays a string and emptiness is `!== undefined`, never truthiness. */
  const activeStatus = applied.intakeStatusID;

  const toggleStatusCard = (statusID: number | null) => {
    const next = statusID === null ? undefined : String(statusID);
    setApplied((prev) => ({
      ...prev,
      intakeStatusID: prev.intakeStatusID === next ? undefined : next,
    }));
    setPage(1);
  };

  const applyFilters = () => {
    setApplied({
      intakeStatusID: draftStatus ?? undefined,
      courseID: draftCourse ?? undefined,
      locationID: draftLocation ?? undefined,
      // ⚠️ `from` is midnight and `to` is end-of-day, so a single-day range covers that whole day.
      // Picking the same date for both would otherwise match only 00:00:00 and return nothing.
      submittedFrom: draftFrom ? Number(toSDKDate(draftFrom)) : undefined,
      submittedTo: draftTo ? Number(toSDKDateTime(endOfDay(draftTo))) : undefined,
    });
    setPage(1);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraftStatus(null); setDraftCourse(null); setDraftLocation(null);
    setDraftFrom(null); setDraftTo(null);
    setApplied({});
    setPage(1);
  };

  const isFilterActive = Boolean(
    applied.intakeStatusID !== undefined || applied.courseID || applied.locationID
    || applied.submittedFrom || applied.submittedTo || debouncedSearch.trim(),
  );

  /**
   * Server-side sort toggle.
   *
   * ⚠️ Resets to page 1: keeping the page number while the whole ordering changes would land the
   * admin in the middle of a different result set.
   */
  const handleSort = (config: SortConfig) => {
    const sortBy = SORT_KEY_BY_COLUMN[config.field];
    if (!sortBy) return; // not a server-sortable column; ignore rather than send a key it will reject
    setSort({ sortBy, sortDir: config.direction === 'desc' ? 'desc' : 'asc' });
    setPage(1);
  };

  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize]);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  /**
   * Opens the approve wizard for a queue row.
   *
   * ⚠️ Only offered on **Pending** rows. Approving something already approved or rejected is not a
   * meaningful action, and the endpoint would reject it.
   */
  const openApprove = useCallback(async (intakeID: number) => {
    setOpeningFor(intakeID);
    try {
      setApproveIntake(await intakeService.getById(intakeID));
    } catch (e) {
      console.error('[Intakes] could not open the approve wizard:', e);
      toast.error('Could not open', 'The intake could not be loaded.');
    } finally {
      setOpeningFor(null);
    }
  }, [intakeService]);

  /**
   * Single-row delete. ⚠️ Capability-detected separately from the bulk flow — `delete` may land before
   * `deleteMany`, and the row action should work the moment either does.
   */
  const canDeleteOne = typeof (intakeService as { delete?: unknown }).delete === 'function';

  // ⚠️ Deliberately does **not** catch: `RowDeleteAction` classifies the failure and keeps its dialog
  // open, so swallowing the error here would close it and report nothing.
  const deleteOne = useCallback(async (intakeID: number) => {
    await (intakeService as unknown as { delete: (id: number) => Promise<unknown> }).delete(intakeID);
    await Promise.all([fetchRows(), fetchCounts()]);
    toast.success('Deleted', 'The intake was removed from the queue.');
  }, [intakeService, fetchRows, fetchCounts]);

  /** Approved rows in the current selection, so the confirmation can say they will be skipped. */
  const selectedApproved = useMemo(
    () => rows.filter((r) => bulk.isSelected(r.intakeID)
      && r.intakeStatusID === INTAKE_STATUS.Approved).length,
    [rows, bulk],
  );

  const columns: TableColumn<IntakeRow>[] = useMemo(() => [
    // Only present in selection mode, so the table is unchanged the rest of the time.
    ...(bulk.selecting ? [{
      key: 'select',
      header: '',
      accessor: (r: IntakeRow) => r.intakeID,
      sortable: false,
      width: 44,
      render: (_: unknown, r: IntakeRow) => (
        <Checkbox
          checked={bulk.isSelected(r.intakeID)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => bulk.toggle(r.intakeID)}
        />
      ),
    } as TableColumn<IntakeRow>] : []),
    {
      key: 'reference',
      header: 'Reference',
      accessor: (r) => r.reference ?? `#${r.intakeID}`,
      sortable: false,
      render: (value, r) => (
        <Stack gap={0}>
          <Text size="sm" fw={600}>{value as string}</Text>
          <Text size="xs" c="dimmed">#{r.intakeID}</Text>
        </Stack>
      ),
    },
    {
      key: 'fullName',
      header: 'Applicant',
      accessor: (r) => r.fullName ?? [r.firstName, r.surname].filter(Boolean).join(' '),
      sortable: true,
      render: (value, r) => (
        <Stack gap={2}>
          <Text size="sm" fw={600}>{(value as string) || 'Unknown'}</Text>
          {r.isExistingMember
            ? <Badge size="xs" variant="light" color="teal">Existing member</Badge>
            : <Text size="xs" c="dimmed">New contact</Text>}
        </Stack>
      ),
    },
    {
      key: 'courseName',
      header: 'Course',
      accessor: (r) => r.courseName ?? '—',
      sortable: true,
      render: (value, r) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.courseColor)}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'startDisplay',
      header: 'Intake starts',
      accessor: (r) => r.startDisplay ?? formatSDKDate(r.startDateTime),
      sortable: true,
    },
    {
      key: 'locationName',
      header: 'Location',
      accessor: (r) => r.locationName ?? '—',
      // Not in SORT_KEY_BY_COLUMN: the API offers no location ordering. Filterable, not sortable.
      sortable: false,
    },
    {
      key: 'participantCount',
      header: 'People',
      accessor: (r) => r.participantCount ?? 0,
      sortable: false,
      align: 'center',
    },
    {
      key: 'submittedDateTime',
      header: 'Submitted',
      accessor: (r) => r.submittedDateTime,
      sortable: true,
      render: (_, r) => (
        <Stack gap={0}>
          <Text size="sm">{formatSDKDate(r.submittedDateTime)}</Text>
          {r.ageDisplay && <Text size="xs" c="dimmed">{r.ageDisplay} ago</Text>}
        </Stack>
      ),
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (r) => r.statusName ?? '—',
      sortable: true,
      align: 'center',
      render: (value, r) => (
        <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(r.statusColor)}>
          {value as string}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (r) => r,
      sortable: false,
      align: 'center',
      render: (_, r) => (
        <Group gap={4} justify="center" wrap="nowrap">
          {r.intakeStatusID === INTAKE_STATUS.Pending && (
            <Tooltip label="Approve and enrol" withArrow>
              <Button
                variant="light"
                color="teal"
                size="xs"
                loading={openingFor === r.intakeID}
                onClick={(e) => { e.stopPropagation(); openApprove(r.intakeID); }}
              >
                <IconCheck size={16} />
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Open" withArrow>
            <Button
              variant="light"
              color="blue"
              size="xs"
              onClick={(e) => { e.stopPropagation(); navigate(`/Grow/Intakes/${r.intakeID}`); }}
            >
              <IconEye size={16} />
            </Button>
          </Tooltip>
          <AccessControl roles={['Admin']} mode='hide'>
          <RowDeleteAction
            id={r.intakeID}
            entityLabel="intake"
            blockedReason={r.intakeStatusID === INTAKE_STATUS.Approved
              ? 'Approved intakes cannot be deleted — a registration exists behind this one. Cancel it instead.'
              : null}
            recoverable
            recordLabel={r.reference ?? `intake #${r.intakeID}`}
            confirmWord={deleteSettings.confirmWord}
            allowed={deleteSettings.allowDelete}
            supported={canDeleteOne}
            onDelete={deleteOne}
          />
          </AccessControl>
        </Group>
      ),
    },
  ], [navigate, openApprove, openingFor, bulk, deleteSettings, canDeleteOne, deleteOne]);

  const kpiCount = statuses.length + 1;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <div>
          <Title order={2}>Intakes</Title>
          <Text c="dimmed">Course sign-ups submitted from the public site, awaiting approval.</Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon
            variant="subtle"
            size="lg"
            loading={loading}
            onClick={() => { fetchRows(); fetchCounts(); }}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: kpiCount }} spacing="md">
        {countsLoading || statuses.length === 0
          ? Array.from({ length: 5 }).map((_, i) => <StatusKPISkeleton key={i} />)
          : (
            <>
              <StatusKPICard
                label="All"
                count={allCount}
                icon={IconListDetails}
                color="#4dabf7"
                active={activeStatus === undefined}
                onClick={() => toggleStatusCard(null)}
              />
              {statuses.map((s) => (
                <StatusKPICard
                  key={s.statusID}
                  label={s.name ?? `Status ${s.statusID}`}
                  count={counts[String(s.statusID)] ?? 0}
                  icon={getStatusIcon(s.icon ?? undefined)}
                  color={s.color || '#868e96'}
                  active={activeStatus === String(s.statusID)}
                  onClick={() => toggleStatusCard(s.statusID)}
                />
              ))}
            </>
          )}
      </SimpleGrid>

      <Card withBorder radius="md" padding="lg">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by name, reference or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
            <Button
              variant="outline"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
              {isFilterActive && <Badge ml="xs" size="xs" color="blue" circle>!</Badge>}
            </Button>
            {isFilterActive && (
              <Button variant="subtle" color="red" onClick={() => { clearFilters(); setSearchTerm(''); }}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <AccessControl roles={['Admin']} mode='hide'>
          <BulkDeleteControls
            bulk={bulk}
            entityLabel="intakes"
            confirmWord={deleteSettings.confirmWord}
            allowed={deleteSettings.allowBulkDelete}
            recoverable
            skipNote={selectedApproved > 0
              ? `${selectedApproved} of these have already been approved and will be skipped.`
              : undefined}
            onDeleted={async ({ removed, requested, failure }) => {
              await Promise.all([fetchRows(), fetchCounts()]);
              // The failure toast is raised by the control itself; this only reports what went.
              if (failure) return;
              if (removed === 0) {
                toast.warning('Nothing deleted', 'No intakes were removed. Approved intakes are skipped.');
              } else if (removed < requested) {
                // ⚠️ A partial run is the normal case, not an error: the server skips approved rows.
                toast.warning(
                  'Some were skipped',
                  `${removed} of ${requested} removed. The rest had already been approved.`,
                );
              } else {
                toast.success('Deleted', `${removed} intake${removed === 1 ? '' : 's'} removed.`);
              }
            }}
          />
          </AccessControl>
        </Flex>
      </Card>

      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filter Intakes"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          <Select
            label="Status"
            placeholder="All statuses"
            data={statuses.map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` }))}
            value={draftStatus}
            onChange={setDraftStatus}
            clearable
          />
          <Select
            label="Course"
            placeholder="All courses"
            data={courses}
            value={draftCourse}
            onChange={setDraftCourse}
            searchable
            clearable
          />
          <Select
            label="Location"
            placeholder="All locations"
            data={locations}
            value={draftLocation}
            onChange={setDraftLocation}
            searchable
            clearable
          />
          {/* Submitted-date range. Genuinely server-side via submittedFrom/submittedTo — filtering
              dates in the browser would only ever filter the single page the server returned. */}
          <DatePickerInput
            label="Submitted from"
            placeholder="Any date"
            value={draftFrom}
            onChange={setDraftFrom}
            maxDate={draftTo ?? undefined}
            clearable
          />
          <DatePickerInput
            label="Submitted to"
            placeholder="Any date"
            value={draftTo}
            onChange={setDraftTo}
            minDate={draftFrom ?? undefined}
            clearable
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => { clearFilters(); setFiltersOpen(false); }}>
              Clear
            </Button>
            <Button onClick={applyFilters}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      <ApproveIntakeModal
        intake={approveIntake}
        opened={approveIntake !== null}
        onClose={() => setApproveIntake(null)}
        onApproved={async (registrationID) => {
          // Same refresh the detail page does: the row leaves Pending and the KPI counts move.
          await Promise.all([fetchRows(), fetchCounts()]);
          toast.success(
            'Intake approved',
            registrationID
              ? `Registration #${registrationID} was created.`
              : 'The registration was created.',
            registrationID
              ? {
                action: {
                  label: 'View registration',
                  onClick: () => navigate(`/Grow/Registration/RegistrationDetails/${registrationID}`),
                },
              }
              : undefined,
          );
        }}
      />

      <Card withBorder radius="md" padding="lg">
        {!loading && rows.length === 0 ? (
          <EmptyState
            variant={isFilterActive ? 'no-results' : 'no-data'}
            entityLabel="intakes"
            onClearFilters={isFilterActive ? () => { clearFilters(); setSearchTerm(''); } : undefined}
          />
        ) : (
          <>
            <CCMSDataTable
              data={rows}
              columns={columns}
              loading={loading && rows.length === 0}
              onRowClick={(r) => navigate(`/Grow/Intakes/${r.intakeID}`)}
              onSort={handleSort}
              striped
              highlightOnHover
            />
            <ListPagination
              page={page}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              showPageSize
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
